import { ClientJourneyContext } from '../../../shared/services/deal/client-journey-context';
import { ValidationGlobalStore } from '../../../../../utilities/validation/validation-global-store';
import { ClientJourneyValidatorStoreBase } from '../../../shared/validation/client-journey-validator-store-base';
import { SlidesStateNames } from '../../../router/slides-states';
import { BorrowerOtherIncomeValidator } from './borrower-other-income.validator';
import { BorrowerOtherIncome } from '../../../shared/models/borrower-other-income.model';
import { BorrowerEmployment } from '../../../shared/models/deal-resources/borrower-employment.model';
import { BorrowerEmploymentValidator } from './borrower-employment.validator';
import { Borrower } from '../../../shared/models/deal-resources/borrower.model';
import { BorrowerIncomeHistoryValidator } from './borrower-income-history.validator';

export class IncomeValidationStore extends ClientJourneyValidatorStoreBase {
  static readonly globalStoreKey = 'IncomeValidationStore';

  constructor(
    globalStore: ValidationGlobalStore,
    clientJourneyContext: ClientJourneyContext,
  ) {
    super(globalStore, clientJourneyContext, SlidesStateNames.Income);

    // Create other income validator for each borrower
    clientJourneyContext.deal.borrowers.forEach(borrower =>
      borrower.otherIncomes.forEach(otherIncome => {
        this.getBorrowerOtherIncomeValidator(otherIncome);
      }),
    );

    // Create employment validator for each borrower
    clientJourneyContext.deal.borrowers.forEach(borrower =>
      borrower.employments.forEach(employment => {
        if (!employment.selfEmployed) {
          this.getBorrowerEmploymentValidator(employment);
        }
      }),
    );

    // Create income history validator for each borrower
    clientJourneyContext.deal.borrowers.forEach(borrower =>
      this.getBorrowerIncomeHistoryValidator(borrower),
    );
  }

  public getBorrowerOtherIncomeValidator(
    borrowerOtherIncome: BorrowerOtherIncome,
  ): BorrowerOtherIncomeValidator {
    let key = this.getBorrowerOtherIncomeKey(borrowerOtherIncome);
    let validator = this.getValidator(key);
    if (!validator) {
      validator = new BorrowerOtherIncomeValidator(borrowerOtherIncome);
      this.addValidator(key, validator);
    }
    return validator as BorrowerOtherIncomeValidator;
  }

  public getBorrowerEmploymentValidator(
    borrowerEmployment: BorrowerEmployment,
  ): BorrowerEmploymentValidator {
    let key = this.getBorrowerEmploymentKey(borrowerEmployment);
    let validator = this.getValidator(key);
    if (!validator) {
      validator = new BorrowerEmploymentValidator(borrowerEmployment);
      this.addValidator(key, validator);
    }
    return validator as BorrowerEmploymentValidator;
  }

  public getBorrowerIncomeHistoryValidator(
    borrower: Borrower,
  ): BorrowerIncomeHistoryValidator {
    let key = this.getBorrowerIncomeHistoryKey(borrower);
    let validator = this.getValidator(key);
    if (!validator) {
      validator = new BorrowerIncomeHistoryValidator(borrower);
      this.addValidator(key, validator);
    }
    return validator as BorrowerIncomeHistoryValidator;
  }

  private getBorrowerOtherIncomeKey(
    borrowerOtherIncome: BorrowerOtherIncome,
  ): string {
    let key = `borrower_other_income_${borrowerOtherIncome.otherIncomeId}`;
    return key;
  }

  private getBorrowerEmploymentKey(
    borrowerEmployment: BorrowerEmployment,
  ): string {
    let key = `borrower_employment_${borrowerEmployment.employmentId}`;
    return key;
  }

  private getBorrowerIncomeHistoryKey(borrower: Borrower): string {
    let key = `borrower_income_history_${borrower.id}`;
    return key;
  }

  override dispose() {
    super.dispose();
  }
}
